import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import SectionTools from '../components/SectionTools';
import { ASSETS, STOCK_IMAGES } from '../config/assets';
import { 
  MessageSquare, 
  Star, 
  Shield, 
  Gift, 
  BarChart3, 
  Smartphone, 
  Zap,
  ArrowRight,
  Globe,
  Lock,
  Cpu,
  CheckCircle,
  UtensilsCrossed,
  Building2,
  Stethoscope,
  Car,
  Sparkles,
  BookOpen,
} from 'lucide-react';

const HomePage = () => {
  const { t, language } = useLanguage();

  // Navigation sections for floating nav
  const navSections = [
    { id: 'hero', labelEn: 'Home', labelFr: 'Accueil' },
    { id: 'gold-standard', labelEn: 'Gold Standard', labelFr: 'Norme d\'Or' },
    { id: 'zero-integration', labelEn: 'Zero Integration', labelFr: 'Zéro Intégration' },
    { id: 'capabilities', labelEn: 'Capabilities', labelFr: 'Capacités' },
    { id: 'intelligence', labelEn: 'Real-Time Intelligence', labelFr: 'Intelligence Temps Réel' },
    { id: 'cta', labelEn: 'Get Started', labelFr: 'Commencer' },
  ];

  const capabilities = [
    {
      icon: MessageSquare,
      title: t('home.platformTitle'),
      desc: t('home.platformDesc'),
      path: '/platform',
      color: 'bg-white/5 hover:bg-white/10',
      iconColor: 'text-wine',
      span: 'col-span-12 md:col-span-8',
    },
    {
      icon: Star,
      title: t('home.reviewsTitle'),
      desc: t('home.reviewsDesc'),
      path: '/positive-reviews',
      color: 'bg-growthgreen/20 hover:bg-growthgreen/30',
      iconColor: 'text-green-400',
      span: 'col-span-12 md:col-span-4',
    },
    {
      icon: Shield,
      title: t('home.shieldTitle'),
      desc: t('home.shieldDesc'),
      path: '/b-heard',
      color: 'bg-darkblue/30 hover:bg-darkblue/40',
      iconColor: 'text-blue-400',
      span: 'col-span-12 md:col-span-4',
    },
    {
      icon: Gift,
      title: t('home.rr360Title'),
      desc: t('home.rr360Desc'),
      path: '/rr360',
      color: 'bg-white/5 hover:bg-white/10',
      iconColor: 'text-purple-400',
      span: 'col-span-12 md:col-span-4',
    },
    {
      icon: Globe,
      title: t('home.communicationTitle'),
      desc: t('home.communicationDesc'),
      path: '/guest-communication',
      color: 'bg-alertorange/20 hover:bg-alertorange/30',
      iconColor: 'text-orange-400',
      span: 'col-span-12 md:col-span-4',
    },
    {
      icon: BarChart3,
      title: t('home.insightsTitle'),
      desc: t('home.insightsDesc'),
      path: '/insights',
      color: 'bg-white/5 hover:bg-white/10',
      iconColor: 'text-cyan-400',
      span: 'col-span-12 md:col-span-6',
    },
    {
      icon: Smartphone,
      title: t('home.hardwareTitle'),
      desc: t('home.hardwareDesc'),
      path: '/hardware',
      color: 'bg-white/5 hover:bg-white/10',
      iconColor: 'text-amber-400',
      span: 'col-span-12 md:col-span-6',
    },
  ];

  const zeroIntegration = language === 'fr' ? [
    'Zéro intégration nécessaire',
    'Zéro dépendance POS',
    'Fonctionne de manière indépendante',
    'Léger et facile à déployer'
  ] : [
    'Zero integration necessary',
    'Zero POS dependency',
    'Runs independently',
    'Lightweight and easy to deploy'
  ];

  const industries = [
    { icon: UtensilsCrossed, label: language === 'fr' ? 'Restaurants' : 'Restaurants', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20 hover:border-red-500/40' },
    { icon: Building2, label: language === 'fr' ? 'Hôtels' : 'Hotels', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20 hover:border-blue-500/40' },
    { icon: Stethoscope, label: language === 'fr' ? 'Cliniques Médicales' : 'Medical Clinics', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20 hover:border-green-500/40' },
    { icon: Car, label: language === 'fr' ? 'Concessionnaires Auto' : 'Auto Dealerships', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20 hover:border-yellow-500/40' },
    { icon: Sparkles, label: language === 'fr' ? '...et plus' : '...and more', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20 hover:border-purple-500/40' },
  ];

  return (
    <div data-testid="home-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={navSections} />

      {/* ═══════════════════════════════════════
          HERO SECTION — Redesigned
          ═══════════════════════════════════════ */}
      <section 
        id="hero"
        data-testid="hero-section"
        className="relative min-h-[85vh] lg:min-h-[92vh] flex items-center scroll-mt-24 overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#0D0D0D]" />
          {/* Subtle gradient orbs for depth */}
          <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-wine/8 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/3 right-0 w-[400px] h-[400px] bg-wine/5 rounded-full blur-[100px]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center max-w-4xl mx-auto pt-8 lg:pt-0">
            {/* Headline */}
            <h1 
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold text-white leading-[1.1] mb-6"
            >
              {t('home.heroTitle')}{' '}
              <span className="text-wine">{t('home.heroAccent')}</span>
            </h1>

            {/* Subtitle */}
            <p 
              data-testid="hero-subtitle"
              className="text-base sm:text-lg lg:text-xl text-white/60 leading-relaxed mb-10 max-w-3xl mx-auto"
            >
              {t('home.heroSubtitle')}
            </p>

            {/* CTA Buttons — same functionality, same routing */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 lg:mb-20">
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="hero-cta-primary"
                  className="btn-get-started rounded-lg px-8 py-6 text-base font-medium"
                >
                  {t('home.cta')}
                </Button>
              </Link>
              <Link to="/platform">
                <Button 
                  size="lg"
                  variant="outline"
                  data-testid="hero-cta-secondary"
                  className="btn-demo-cta rounded-lg px-8 py-6 text-base font-medium"
                >
                  {t('home.ctaSecondary')}
                </Button>
              </Link>
            </div>

            {/* ─── Industry Section ─── */}
            <div data-testid="industry-section" className="border-t border-white/10 pt-10">
              <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.25em] text-white/30 mb-8">
                {t('home.industryHeading')}
              </p>
              <div className="flex flex-wrap justify-center gap-6 sm:gap-10 lg:gap-14">
                {industries.map((ind, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-2.5 group">
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl ${ind.bg} border ${ind.border} flex items-center justify-center transition-all duration-300`}>
                      <ind.icon className={`w-6 h-6 sm:w-7 sm:h-7 ${ind.color}`} strokeWidth={1.5} />
                    </div>
                    <span className="text-xs sm:text-sm text-white/50 font-medium group-hover:text-white/70 transition-colors">
                      {ind.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          TRUST BAR
          ═══════════════════════════════════════ */}
      <section className="py-6 bg-[#111] border-t border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 sm:gap-12 lg:gap-16">
            {[
              { icon: CheckCircle, text: language === 'fr' ? 'Approuvé par les Marques Leaders' : 'Trusted by Leading Brands', color: 'text-red-400' },
              { icon: Lock, text: language === 'fr' ? 'Sécurisé & Conforme' : 'Secure & Compliant', color: 'text-green-400' },
              { icon: BarChart3, text: language === 'fr' ? 'Informations en Temps Réel' : 'Real-Time Insights', color: 'text-blue-400' },
              { icon: Sparkles, text: language === 'fr' ? 'Meilleures Expériences. Meilleure Entreprise.' : 'Better Experiences. Better Business.', color: 'text-pink-400' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <item.icon className={`w-4 h-4 ${item.color}`} strokeWidth={1.5} />
                <span className="text-xs text-white/50 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          ALL EXISTING SECTIONS BELOW — UNCHANGED
          ═══════════════════════════════════════ */}

      {/* THE GOLD STANDARD Section */}
      <section 
        id="gold-standard"
        data-testid="gold-standard-section"
        className="py-20 lg:py-28 bg-gradient-to-b from-[#0D0D0D] to-[#1A1A1A] border-t border-b border-white/5 scroll-mt-24"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="gold-standard" sectionTitle={language === 'fr' ? 'La Norme d\'Or' : 'The Gold Standard'} />
          </div>
          <div className="mb-8">
            <span className="inline-block px-4 py-2 bg-gold/10 text-gold text-xs font-bold uppercase tracking-[0.25em] rounded-full border border-gold/30">
              {language === 'fr' ? 'La Norme d\'Or' : 'The Gold Standard'}
            </span>
          </div>
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-8 leading-tight">
            {language === 'fr' 
              ? 'Intelligence Axée sur la Confidentialité'
              : 'Privacy-First Intelligence'
            }
          </h2>
          
          <div className="space-y-6 text-lg sm:text-xl text-white/60 leading-relaxed max-w-4xl mx-auto">
            <p>
              {language === 'fr'
                ? "À une époque où les entreprises récoltent des données de manière invasive, nous avons été les pionniers d'une nouvelle philosophie :"
                : "In an era where businesses invasively harvest data, we've pioneered a new philosophy:"
              }
              <span className="text-white font-medium"> {language === 'fr' ? 'L\'Intelligence Axée sur la Confidentialité' : 'Privacy-First Intelligence'}.</span>
            </p>
            <p>
              {language === 'fr'
                ? "Nous fournissons des informations client profondes et critiques directement depuis la source, construites sur une base d'anonymat absolu."
                : "We provide deep, mission-critical client information directly from the source, built on a foundation of absolute anonymity."
              }
            </p>
            <p className="text-white/80 font-medium">
              {language === 'fr'
                ? "En respectant l'individu, nous révélons la vérité."
                : "By respecting the individual, we unlock the truth."
              }
            </p>
          </div>

          <div className="mt-12 flex justify-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 rounded-full border border-white/10">
              <Lock className="w-5 h-5 text-gold" strokeWidth={1.5} />
              <span className="text-white/70 text-sm">
                {language === 'fr' 
                  ? "C'est la nouvelle norme d'intégrité des données"
                  : "This is the new standard of data integrity"
                }
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Zero POS Integration Section */}
      <section 
        id="zero-integration"
        data-testid="zero-integration-section"
        className="py-16 lg:py-20 bg-[#0D0D0D] scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="zero-integration" sectionTitle={language === 'fr' ? 'Zéro Intégration' : 'Zero Integration'} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Cpu className="w-8 h-8 text-wine" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine">
                  {language === 'fr' ? 'Déploiement Indépendant' : 'Independent Deployment'}
                </span>
              </div>
              
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6 leading-tight">
                {language === 'fr' 
                  ? 'Zéro Intégration POS. Zéro Complexité.'
                  : 'Zero POS Integration. Zero Complexity.'
                }
              </h2>
              
              <p className="text-lg text-white/60 leading-relaxed mb-8">
                {language === 'fr'
                  ? "YUL On Tap vit entièrement par lui-même. Pas d'intégrations POS compliquées, pas de changements matériels, et pas de goulets d'étranglement IT. Notre système fonctionne aux côtés de votre entreprise — simple, rapide et indépendant."
                  : "YUL On Tap lives entirely on its own. No messy POS integrations, no hardware changes, and no IT bottlenecks. Our system runs alongside your business — simple, fast, and independent."
                }
              </p>
              
              <ul className="space-y-3">
                {zeroIntegration.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              <img
                src={ASSETS.TAP_ENGINE_CARDS}
                alt="YUL On Tap Engine Cards Dashboard"
                className="rounded-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/50 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section 
        id="capabilities"
        data-testid="capabilities-section"
        className="py-20 lg:py-28 bg-[#0D0D0D] scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="capabilities" sectionTitle={language === 'fr' ? 'Capacités' : 'Capabilities'} />
          </div>
          <div className="text-center mb-12 lg:mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine mb-4 block">
              {t('home.capabilitiesTitle')}
            </span>
            <h2 
              data-testid="capabilities-title"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4"
            >
              {t('home.capabilitiesSubtitle')}
            </h2>
          </div>

          <div className="grid grid-cols-12 gap-4 lg:gap-6">
            {capabilities.map((cap, idx) => (
              <Link
                key={idx}
                to={cap.path}
                data-testid={`capability-card-${idx}`}
                className={`${cap.span} group`}
              >
                <div className={`h-full ${cap.color} border border-white/10 rounded-2xl p-6 lg:p-8 hover:-translate-y-1 hover:border-white/20 transition-all duration-300`}>
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white/5 rounded-xl group-hover:bg-white/10 transition-colors">
                      <cap.icon className={`w-6 h-6 ${cap.iconColor}`} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-medium text-white mb-2">
                        {cap.title}
                      </h3>
                      <p className="text-white/50 text-sm leading-relaxed">
                        {cap.desc}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-wine group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                  </div>
                </div>
              </Link>
            ))}
            
            {/* Implementation Card - Special Styling */}
            <Link
              to="/implementation"
              data-testid="capability-card-implementation"
              className="col-span-12 group"
            >
              <div className="h-full bg-darkblue/20 border border-white/10 rounded-2xl p-6 lg:p-8 hover:-translate-y-1 hover:border-white/20 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/5 rounded-xl group-hover:bg-white/10 transition-colors">
                    <Zap className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-medium text-white mb-2">
                      {t('home.implementationTitle')}
                    </h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      {t('home.implementationDesc')}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Real-Time Intelligence Section */}
      <section 
        id="intelligence"
        data-testid="intelligence-section"
        className="py-20 lg:py-28 bg-[#1A1A1A] scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end mb-4">
            <SectionTools sectionId="intelligence" sectionTitle={language === 'fr' ? 'Intelligence Temps Réel' : 'Real-Time Intelligence'} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                                src={ASSETS.DASHBOARD_MAIN}
                alt="YUL On Tap Dashboard"
                className="rounded-2xl w-full object-cover shadow-2xl border border-white/10"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-alertorange mb-4 block">
                {language === 'fr' ? 'Intelligence en Temps Réel' : 'Real-Time Intelligence'}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6 leading-tight">
                {language === 'fr' 
                  ? 'Connaissance Opérationnelle Instantanée'
                  : 'Instant Operational Awareness'
                }
              </h2>
              <p className="text-lg text-white/60 leading-relaxed mb-8">
                {language === 'fr'
                  ? "Chaque interaction client, chaque demande de service, chaque moment de récupération — capturé et acheminé vers les bonnes personnes en temps réel."
                  : "Every client interaction, every service request, every recovery moment — captured and routed to the right people in real-time."
                }
              </p>
              <Link to="/platform">
                <Button 
                  className="bg-wine hover:bg-wine-light text-white rounded-lg"
                >
                  {language === 'fr' ? 'Explorer la Plateforme' : 'Explore the Platform'}
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          SUPPORTING DATA TEASER
          ═══════════════════════════════════════ */}
      <section 
        data-testid="supporting-data-teaser"
        className="py-16 lg:py-20 bg-[#0D0D0D] border-t border-white/5"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-6">
            <BookOpen className="w-3.5 h-3.5 text-white/50" strokeWidth={1.5} />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
              {t('home.supportingDataHeading')}
            </span>
          </div>
          <p className="text-lg text-white/50 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t('home.supportingDataText')}
          </p>
          <Link to="/supporting-data">
            <Button 
              variant="outline"
              data-testid="supporting-data-teaser-cta"
              className="bg-white/5 text-white/70 border-white/15 hover:bg-white/10 hover:text-white hover:border-white/25 rounded-lg px-6 py-5 text-sm font-medium"
            >
              {t('home.supportingDataCta')}
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta"
        data-testid="cta-section"
        className="py-20 lg:py-28 bg-gradient-to-b from-[#1A1A1A] to-[#0D0D0D] scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            data-testid="cta-title"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4"
          >
            {t('home.ctaTitle')}
          </h2>
          <p className="text-lg text-white/50 mb-8 max-w-2xl mx-auto">
            {t('home.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg"
                data-testid="cta-btn-primary"
                className="bg-wine hover:bg-wine-light text-white rounded-lg px-8 py-6 text-base font-medium"
              >
                {t('home.cta')}
              </Button>
            </Link>
            <Link to="/platform">
              <Button 
                size="lg"
                variant="outline"
                data-testid="cta-btn-secondary"
                className="btn-demo-cta rounded-lg px-8 py-6 text-base font-medium"
              >
                {t('home.ctaSecondary')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
